import { useCallback, useEffect, useState } from "react";
import { ConfirmModal } from "../lib/ConfirmModal.tsx";
import { fetchJson } from "../lib/fetchJson.ts";
import { day, Refusal } from "./bits.tsx";

// The Operators screen: every platform operator with its state, an invite
// form, and Remove behind a confirmation. No mail goes out on an invite; the
// invitee accepts by signing in. See ADR-0039.

/** An operator row's state: invited and not yet signed in, accepted, or removed. */
export type OperatorState = "invited" | "accepted" | "removed";

/** One platform operator as `GET /api/operators` lists it. A removed row stays listed as the record. */
export type OperatorRow = {
  id: string;
  email: string;
  state: OperatorState;
  invitedBy: string | null;
  invitedAt: string;
  acceptedAt: string | null;
  removedAt: string | null;
};

type OperatorsPageProps = {
  me: { email: string };
};

const STATE_PILL: Record<OperatorState, string> = {
  invited: "pill warn",
  accepted: "pill ok",
  removed: "pill plain",
};

/**
 * The Operators screen: the operator table with Remove on each live row and
 * the invite form beneath. Reads `/api/operators` on mount and after every
 * invite or remove; removing yourself sends the browser to sign-in. Rendered
 * by the admin shell at `/operators`.
 */
export function OperatorsPage({ me }: OperatorsPageProps): JSX.Element {
  const [rows, setRows] = useState<OperatorRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await fetchJson<{ operators: OperatorRow[] }>("/api/operators");
      setRows(body.operators);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  let table: JSX.Element;
  if (rows === null) {
    table = <p aria-live="polite">Loading…</p>;
  } else {
    table = (
      <table className="grid">
        <thead>
          <tr>
            <th>Email</th>
            <th>State</th>
            <th>Invited by</th>
            <th>Invited</th>
            <th>Accepted</th>
            <th>Removed</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <OperatorLine key={o.id} o={o} self={o.email === me.email && o.state !== "removed"} onChanged={load} />
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <>
      <p className="lede">
        The people who administer the platform. An invitation is accepted the first time the
        invitee signs in with their company Google account; one not accepted within seven days
        expires. Removing an operator ends their sessions at once.
      </p>

      <Refusal message={loadError} />

      <section className="panel" aria-label="operators">
        <div className="panel-head">
          <h2>Operators</h2>
          {rows ? <span className="meta">{rows.filter((o) => o.state !== "removed").length} live</span> : null}
        </div>
        <div className="panel-body flush scroll-x">{table}</div>
      </section>

      <InviteForm onInvited={load} />
    </>
  );
}

function OperatorLine({
  o,
  self,
  onChanged,
}: {
  o: OperatorRow;
  self: boolean;
  onChanged: () => Promise<void>;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fetchJson(`/api/operators/${encodeURIComponent(o.id)}/remove`, { method: "POST" });
      if (self) {
        // The session this page runs under is one of the ones just ended.
        window.location.assign("/sign-in");
        return;
      }
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <tr>
      <td>
        {o.email}
        {self ? (
          <>
            {" "}
            <span className="tag">you</span>
          </>
        ) : null}
      </td>
      <td>
        <span className={STATE_PILL[o.state]}>{o.state}</span>
      </td>
      <td>{o.invitedBy ?? "—"}</td>
      <td>{day(o.invitedAt)}</td>
      <td>{day(o.acceptedAt)}</td>
      <td>{day(o.removedAt)}</td>
      <td>
        {o.state === "removed" ? null : (
          <>
            <button type="button" className="btn ghost xs" onClick={() => setConfirming(true)}>
              Remove
            </button>
            <ConfirmModal
              open={confirming}
              title="Remove operator"
              heading={<>Remove {o.email}?</>}
              confirmLabel="Confirm remove"
              busyLabel="Removing…"
              busy={busy}
              error={error}
              onConfirm={() => void remove()}
              onCancel={() => setConfirming(false)}
            >
              <p>
                Every session they hold ends now, and the row stays as the record.
                {self ? " This is you: you will be signed out of this app." : ""}
              </p>
            </ConfirmModal>
          </>
        )}
        {confirming ? null : <Refusal message={error} />}
      </td>
    </tr>
  );
}

function InviteForm({ onInvited }: { onInvited: () => Promise<void> }): JSX.Element {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invited, setInvited] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setError(null);
    setInvited(null);
    try {
      const body = await fetchJson<{ operator: OperatorRow }>("/api/operators", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setInvited(body.operator.email);
      setEmail("");
      await onInvited();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="panel pad" aria-label="invite an operator">
      <form onSubmit={onSubmit}>
        <h2>Invite</h2>
        <p className="sub">
          A company Workspace address. Nothing is emailed: tell them to sign in at this address
          within seven days.
        </p>
        <div className="field">
          <label htmlFor="invite-operator-email">Email</label>
          <input
            id="invite-operator-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <Refusal message={error} />
        <div className="btn-row">
          <button type="submit" className="btn primary" disabled={!email || submitting}>
            {submitting ? "Inviting…" : "Invite"}
          </button>
        </div>
        {invited ? (
          <p className="hint" aria-live="polite">
            Invited {invited}. Their first sign-in accepts it.
          </p>
        ) : null}
      </form>
    </section>
  );
}
